import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Swords, Loader2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { UserAvatar } from "@/components/UserBits";
import { Slider } from "@/components/ui/slider";

const LABEL = { pending: "Wartet auf Annahme", live: "Läuft gerade", done: "Beendet", expired: "Abgelaufen", declined: "Abgelehnt", cancelled: "Zurückgezogen" };

function DuelRow({ d }) {
  const tag = d.status === "done" ? (d.result === "win" ? ["Gewonnen", "text-emerald-300"] : d.result === "lose" ? ["Verloren", "text-rose-300"] : ["Unentschieden", "text-white/60"]) : [d.incoming ? "Fordert dich heraus!" : LABEL[d.status], d.incoming || d.status === "live" ? "text-amber-300 animate-pulse" : "text-white/50"];
  return (
    <Link to={`/spiele/tresor-duell/${d.id}`} data-testid={`vault-duel-row-${d.id}`} className="flex items-center gap-3 rounded-2xl bg-white/5 hover:bg-white/10 px-3 py-2 transition-colors">
      <UserAvatar userId={d.other.user?.user_id} name={d.other.user?.name} picture={d.other.user?.picture} size="w-9 h-9" text="text-xs" />
      <div className="min-w-0 flex-1"><p className="text-sm font-bold truncate">{d.other.user?.name || "Freund"}</p><p className={`text-xs font-bold ${tag[1]}`}>{tag[0]} · {d.stake} Punkte</p></div>
      <ChevronRight className="w-4 h-4 text-white/40" />
    </Link>
  );
}

export function VaultDuelPanel() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [friends, setFriends] = useState([]);
  const [pick, setPick] = useState(null);
  const [stake, setStake] = useState(10);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/vault-duel").then(({ data }) => { setD(data); setStake((s) => Math.min(data.max_stake, Math.max(data.min_stake, s))); }).catch(() => setD(null));
  useEffect(() => {
    load();
    api.get("/vault/friends").then(({ data }) => setFriends(data.friends)).catch(() => {});
    const iv = setInterval(() => document.visibilityState === "visible" && load(), 15000);
    return () => clearInterval(iv);
  }, []);
  if (!d) return null;
  const start = async () => {
    setBusy(true);
    try { const { data } = await api.post("/vault-duel/challenge", { friend_id: pick, stake }); nav(`/spiele/tresor-duell/${data.id}`); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-rose-950 text-white p-5 space-y-4 ring-1 ring-rose-400/30" data-testid="vault-duel-panel">
      <div>
        <h3 className="font-display text-lg font-extrabold flex items-center gap-2"><Swords className="w-5 h-5 text-rose-300" /> Tresor-Duell – live gegen Freunde</h3>
        <p className="text-sm text-white/60">Ihr knackt denselben Mini-Tresor gleichzeitig. {Math.round(d.seconds / 60)} Minuten, max. {d.max_tries} Versuche – wer weniger Versuche braucht, gewinnt beide Einsätze. Noch {d.left_today} Duell{d.left_today === 1 ? "" : "e"} heute.</p>
      </div>
      {d.duels.length > 0 && <div className="space-y-1.5">{d.duels.map((x) => <DuelRow key={x.id} d={x} />)}</div>}
      {d.left_today === 0 && <p className="text-sm font-bold text-amber-200" data-testid="vault-duel-limit">Für heute hast du alle Duelle gestartet – morgen geht's weiter. Herausforderungen annehmen kannst du trotzdem.</p>}
      {friends.length === 0 && d.left_today > 0 && <Link to="/einladen" className="inline-flex h-10 px-4 rounded-full bg-white/10 text-sm font-bold items-center" data-testid="vault-duel-no-friends">Füge Freunde hinzu, um sie herauszufordern</Link>}
      {friends.length > 0 && d.left_today > 0 && (
        <div className="space-y-3">
          <ul className="flex gap-2 overflow-x-auto pb-1">
            {friends.map((f) => (
              <li key={f.user_id}><button onClick={() => setPick(f.user_id)} data-testid={`vault-duel-pick-${f.user_id}`} className={`w-20 rounded-2xl p-2 text-center transition-colors ${pick === f.user_id ? "bg-rose-500/40 ring-2 ring-rose-300" : "bg-white/5"}`}>
                <UserAvatar userId={f.user_id} name={f.name} picture={f.picture} size="w-11 h-11 mx-auto" text="text-sm" /><p className="mt-1 text-[11px] font-bold truncate">{f.name}</p>
              </button></li>
            ))}
          </ul>
          <div className="space-y-2">
            <p className="text-sm font-bold flex justify-between"><span>Einsatz</span><span className="text-amber-300" data-testid="vault-duel-stake">{stake} Punkte</span></p>
            <Slider min={d.min_stake} max={d.max_stake} step={5} value={[stake]} onValueChange={([v]) => setStake(v)} data-testid="vault-duel-stake-slider" />
          </div>
          <button onClick={start} disabled={!pick || busy} data-testid="vault-duel-start" className="w-full h-12 rounded-full bg-gradient-to-r from-rose-500 to-amber-500 font-extrabold inline-flex items-center justify-center gap-2 disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Swords className="w-4 h-4" />} Herausfordern
          </button>
        </div>
      )}
    </div>
  );
}
